import { useEffect, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, formatINR, formatDate, formatApiError } from "@/lib/api";
import { useLiveRefresh } from "@/lib/live";
import { PageHeader, StatusBadge, LotTag, PipelineTag, QueueNo, badgeStatus, excelStageStatus, readyForLabel, stageKey as statusKey, DEPT_STATUSES, UrgentBadge } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Search, ClipboardPaste, ClipboardList, AlertTriangle, X, ArrowRightCircle, FileText, Eye, Download } from "lucide-react";
import { toast } from "sonner";
import { useAuth, hasRole } from "@/context/AuthContext";
import { viewFor } from "@/config/roleViews";
import { SelectWithAdd } from "@/components/SelectWithAdd";
import MasterJobCardForm from "@/components/MasterJobCardForm";
import BulkStageImport from "@/components/BulkStageImport";
import PlanningImport from "@/components/orders/PlanningImport";
import QuickCustomerDialog from "@/components/orders/QuickCustomerDialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { MACHINE_DEPTS } from "@/lib/employees";

// Stale-inquiry threshold. Any New Inquiry older than this many days without an
// order_type set is flagged in the list + counted in the follow-up nudge.
const STALE_THRESHOLD_DAYS = 5;
const PAGE_SIZE = 100;
const PRODUCTION_STAGE_STATUSES = [
  ["stores_issue", "in_production"],
  ["printing", "printing"],
  ["flatbed", "flat_bed"],
  ["avt", "avt"],
  ["slitting", "slitting"],
  ["quality", "qc"],
  ["packing", "packing"],
  ["coa", "ready_to_dispatch"],
  ["dispatch", "dispatched"],
];
const PRODUCTION_STAGE_ORDER = PRODUCTION_STAGE_STATUSES.map(([stageKey]) => stageKey);
const ALL_DEPT_STATUS_KEYS = new Set(Object.values(DEPT_STATUSES).flatMap((d) => d.statuses));
// Pending (To be planned / Planning) is Planning's queue: listed in its order and numbered like a department's.
const PLANNING_KEYS = ["to_be_planned", "planning"];
const isPlanningFilter = (f) => f !== "all" && !f.includes(":") && f.split(",").every((s) => PLANNING_KEYS.includes(statusKey(s)));

// Where a job still marked Converted BPR is, by its BPR Status (the Jobs Pipeline's own column) — and a
// "Not in Pipeline" job by its status, which is the BPR Status sheet's stage name.
const BPR_STAGE_TO_FLOW = {
  printing: "printing", part_qty_printing: "printing", partially_printed: "printing",
  flat_bed: "flatbed", flatbed: "flatbed", avt: "avt", part_qty_avt: "avt",
  slitting: "slitting", part_qty_slitting: "slitting", numbering: "slitting", qc: "quality", quality: "quality",
  packing: "packing", packing_fg: "packing", part_qty_packing: "packing",
  ready_to_dispatch: "dispatch", dispatched: "dispatch", part_qty_disp: "dispatch",
};

function getProductionFocusStage(job) {
  const status = (job.status || "").trim().toLowerCase().replace(/\s+/g, "_");
  const stageOf = (v) => BPR_STAGE_TO_FLOW[(v || "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "_").replace(/^_+|_+$/g, "")];
  if (status === "converted_bpr" && stageOf(job.bpr_status)) return stageOf(job.bpr_status);
  if (excelStageStatus(job) && stageOf(job.status)) return stageOf(job.status);
  // A job already dispatched or cancelled is done, full stop — show its last stage. Trusting per-stage
  // records here is wrong for most jobs: they were bulk-imported with a status but no "dispatch" stage
  // record ever filled in (not even one of the 9,917 dispatched jobs in this system has one), so the
  // stage-completion scan below would otherwise land back on the very first incomplete stage it finds —
  // often Stores Issue — even though the job genuinely shipped.
  if (status === "dispatched" || status === "cancelled" || status === "canceled") return "dispatch";
  const stageRecords = job.stages || {};
  if (PRODUCTION_STAGE_ORDER.some((stageKey) => stageRecords[stageKey])) {
    // Jobs that were already past Slitting before Quality Check existed have no quality record: skip it.
    const laterStarted = PRODUCTION_STAGE_ORDER.slice(PRODUCTION_STAGE_ORDER.indexOf("quality") + 1).some((k) => stageRecords[k]);
    return PRODUCTION_STAGE_ORDER.find((stageKey) => !stageRecords[stageKey]?.completed_at && !(stageKey === "quality" && laterStarted)) || "dispatch";
  }
  const statusToStage = {
    in_production: "printing",
    printing: "flatbed",
    flat_bed: "avt",
    avt: "slitting",
    slitting: "quality",
    qc: "packing",
    packing: "coa",
    ready_to_dispatch: "dispatch",
    dispatched: "dispatch",
  };
  return statusToStage[status] || "stores_issue";
}

function getJobDisplayStatus(job) {
  const originalStatus = job.status || "pending";
  const normalizedStatus = originalStatus.trim().toLowerCase().replace(/\s+/g, "_");
  if (["cancelled", "canceled", "hold", "on_hold"].includes(normalizedStatus)) {
    return originalStatus;
  }
  // Part of the quantity printed so far: stays "Partially Printed" until the Printing stage is completed.
  if (["partially_printed", "part_qty_printing"].includes(normalizedStatus) && !job.stages?.printing?.completed_at) {
    return originalStatus;
  }
  const completedStatus = PRODUCTION_STAGE_STATUSES.reduce(
    (latest, [stageKey, status]) => job.stages?.[stageKey]?.completed_at ? status : latest,
    null,
  );
  return completedStatus || originalStatus;
}

function isStaleInquiry(j) {
  if (!j || !j.created_at) return false;
  // Must be inquiry-side (New Inquiry explicitly, or legacy new via job_type / bpr_status)
  const inq = (j.inquiry_type || "").trim();
  const jt = (j.job_type || "").trim().toLowerCase();
  const bpr = (j.bpr_status || "").trim().toLowerCase();
  const isNew = inq === "New Inquiry" || (!inq && (jt.startsWith("new") || bpr.startsWith("new")));
  if (!isNew) return false;
  // Only stale if no order_type set and not already dispatched/cancelled
  if ((j.order_type || "").trim()) return false;
  const st = (j.status || "").toLowerCase();
  if (st === "dispatched" || st === "cancelled") return false;
  const age = (Date.now() - new Date(j.created_at).getTime()) / 86400000;
  return age > STALE_THRESHOLD_DAYS;
}

export default function Orders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  // Dashboard tiles and other screens open the list pre-filtered (?status=, ?view=, ?q=, ?stale=1).
  const [searchParams] = useSearchParams();
  const [jobs, setJobs] = useState([]);
  const [editingRank, setEditingRank] = useState(null);  // job id whose "#" cell is being edited
  const [statusList, setStatusList] = useState([]);
  // "dept:x" (a Jobs in X tile) and "ready:a,b" (a desk tile) place Converted BPR jobs by BPR Status.
  const [statusFilter, setStatusFilter] = useState(() => (
    searchParams.get("dept") ? `dept:${searchParams.get("dept")}`
      : searchParams.get("ready") ? `ready:${searchParams.get("ready")}`
        : searchParams.get("status") || "all"
  ));
  const [deptCounts, setDeptCounts] = useState({});
  const [inquiryFilter, setInquiryFilter] = useState(() => searchParams.get("inquiry_type") || "all");
  const [orderTypeFilter, setOrderTypeFilter] = useState(() => searchParams.get("order_type") || "all");
  const [viewMode, setViewMode] = useState(() => (
    searchParams.get("pipeline") === "missing" ? "missing"
      : ["inquiries", "orders"].includes(searchParams.get("view")) ? searchParams.get("view") : "all"
  )); // "all" | "inquiries" | "orders" | "missing" (jobs with no row in the Jobs Pipeline sheet)
  const [staleOnly, setStaleOnly] = useState(() => searchParams.get("stale") === "1");
  const [page, setPage] = useState(0);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ all: 0, inquiries: 0, orders: 0, not_in_pipeline: 0 });
  // The department (or Planning) queue the list is in, when it is one: its jobs carry their number in it.
  const [queue, setQueue] = useState({ name: null, total: 0 });
  const requestSeq = useRef(0);
  const [q, setQ] = useState(() => searchParams.get("q") || "");
  const [open, setOpen] = useState(false);
  const [inqOpen, setInqOpen] = useState(false);            // New Inquiry dialog
  const [pasteOpen, setPasteOpen] = useState(false);        // Bulk Import (Excel paste) dialog
  const [previewJob, setPreviewJob] = useState(null);       // Job whose Master Job Card PDF is being previewed
  const [previewUrl, setPreviewUrl] = useState(null);       // Blob URL for the previewed PDF
  const [previewLoading, setPreviewLoading] = useState(false);
  const [convertFor, setConvertFor] = useState(null);       // Job being converted, or null
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  // Bulk edit state
  const [selected, setSelected] = useState(() => new Set());
  const [machines, setMachines] = useState([]);
  const [operators, setOperators] = useState([]);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  // Stale-inquiry nudge count (from server, not client-filtered)
  const [staleCount, setStaleCount] = useState(0);
  // Operations Head is the one operator-base role that can create jobs/inquiries — it schedules
  // across every department, so it needs to add new work the same way it can already see all of it.
  const canCreate = hasRole(user, "production_manager", "accounts") || user?.role === "operations_head";
  const canBulk = hasRole(user, "admin", "production_manager", "accounts");
  // The schedule (queue order, urgent) is the operations head's; everyone else sees the numbers only.
  const canSchedule = user?.role === "operations_head";
  const canSeeFinance = viewFor(user).finance; // money columns follow the role view (config/roleViews.js)

  // `quiet` is the background refresh (live updates): no spinner, no error toast, and
  // ticked rows stay ticked while they are still on the page.
  const load = ({ quiet = false } = {}) => {
    const params = { limit: PAGE_SIZE, skip: page * PAGE_SIZE, with_total: true };
    // Scheduling Jobs with no department picked: the server still sends each job's real place in whichever
    // queue it is in, so the number holds when a search narrows which rows are shown (not just this page's).
    if (canSchedule) params.with_queue_no = true;
    if (statusFilter.startsWith("dept:")) params.dept = statusFilter.slice(5);
    else if (statusFilter.startsWith("ready:")) params.ready = statusFilter.slice(6);
    else if (statusFilter !== "all") {
      params.status = statusFilter;
      if (isPlanningFilter(statusFilter)) params.queue = "planning";
    }
    if (inquiryFilter !== "all") params.inquiry_type = inquiryFilter;
    if (orderTypeFilter !== "all") params.order_type = orderTypeFilter;
    // The list is the Jobs Pipeline's: a job with no row there shows only under "Not in Pipeline".
    if (viewMode === "missing") params.pipeline = "missing";
    else {
      params.pipeline = "listed";
      if (viewMode !== "all") params.view = viewMode;
    }
    if (staleOnly) params.stale_days = STALE_THRESHOLD_DAYS;
    if (q) params.q = q;
    const seq = ++requestSeq.current;
    if (!quiet) {
      setLoading(true);
      setLoadError(null);
      setSelected(new Set());  // a new query clears selection to avoid stale ids
    }
    return api.get("/jobs", { params })
      .then(({ data }) => {
        if (seq !== requestSeq.current) return;  // superseded by a newer request
        if (!data.items.length && page > 0) {
          // Page emptied (e.g. by a bulk status change): go to the last page.
          setPage(Math.max(0, Math.ceil(data.total / PAGE_SIZE) - 1));
          return;
        }
        setJobs(data.items);
        setTotal(data.total);
        setCounts(data.counts);
        setQueue({ name: data.queue || null, total: data.queue_total || 0 });
        setLoadError(null);
        if (quiet) {
          const listed = new Set(data.items.map((j) => j.id));
          setSelected((s) => new Set([...s].filter((id) => listed.has(id))));
        }
      })
      .catch((e) => {
        if (seq !== requestSeq.current || quiet) return;
        setJobs([]);
        setLoadError(formatApiError(e));
        toast.error(`Failed to load jobs: ${formatApiError(e)}`);
      })
      .finally(() => { if (seq === requestSeq.current) setLoading(false); });
  };

  const loadStats = () => {
    const pipeline = viewMode === "missing" ? "missing" : "listed";
    api.get("/jobs/stats/statuses", { params: { pipeline } }).then(({ data }) => setStatusList(data)).catch(() => {});
    api.get("/jobs/stats/departments", { params: { pipeline } }).then(({ data }) => setDeptCounts(data || {})).catch(() => {});
    api.get(`/jobs/stats/stale-inquiries?threshold_days=${STALE_THRESHOLD_DAYS}`)
      .then(({ data }) => setStaleCount(data.count || 0)).catch(() => {});
  };

  // Any filter change resets to the first page.
  const onFilter = (setter) => (value) => { setPage(0); setter(value); };
  const search = () => (page === 0 ? load() : setPage(0));

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, inquiryFilter, orderTypeFilter, viewMode, staleOnly, page]);
  // Jobs created, edited, moved through stages or imported by anyone; customer renames.
  const reload = useLiveRefresh(["jobs", "customers"], () => { loadStats(); return load({ quiet: true }); });
  useEffect(() => { loadStats(); /* eslint-disable-next-line */ }, [viewMode]);
  useEffect(() => {
    if (canBulk) {
      api.get("/machines").then(({ data }) => setMachines(data)).catch(() => {});
      api.get("/operators").then(({ data }) => setOperators(data.filter(o => o.status === "active" && MACHINE_DEPTS.includes(o.department)))).catch(() => {});
    }
  }, [canBulk]); // eslint-disable-line react-hooks/exhaustive-deps

  // A click anywhere on a row opens the job; links, buttons and checkboxes inside keep their own action.
  const openRow = (e, job) => {
    if (e.target.closest("a, button, input, select, label, textarea")) return;
    const to = `/orders/${job.id}`;
    if (e.metaKey || e.ctrlKey) window.open(to, "_blank");
    else navigate(to);
  };

  // The server sends a department's (or Planning's) list in its queue order, each job with its number there.
  const visibleJobs = jobs;

  // Put a job at a new number in its queue: the server renumbers the whole queue (urgent jobs stay ahead), and
  // every desk showing it picks the new numbers up through the live refresh. `queueName` is the row's own
  // queue — on a single department's list that's `queue.name`; on the mixed Scheduling Jobs list, each row
  // can be in a different department's queue, so it travels with the row instead (`j.queue`).
  const reorderJob = async (jobId, newPosRaw, queueName) => {
    setEditingRank(null);
    const position = Math.round(Number(newPosRaw));
    if (!queueName || !Number.isFinite(position) || position < 1) return;
    try {
      const { data } = await api.post(`/jobs/${jobId}/queue-position`, { queue: queueName, position });
      if (data.position !== position) {
        toast.info(position > data.total ? `Placed last, at #${data.position}.`
          : `Placed at #${data.position}: urgent jobs are kept ahead of the scheduled ones.`);
      }
    } catch (e) {
      toast.error(`Couldn't save the new order: ${formatApiError(e)}`);
    }
    load({ quiet: true });
  };

  // Urgent jobs go to the top of their department's queue (in every desk's list too), in the order set here.
  const toggleUrgent = async (job) => {
    const next = !job.urgent_schedule;
    setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, urgent_schedule: next } : j)));
    try {
      await api.patch(`/jobs/${job.id}`, { urgent_schedule: next });
      load({ quiet: true });
    } catch {
      toast.error("Couldn't save urgent flag");
      setJobs((prev) => prev.map((j) => (j.id === job.id ? { ...j, urgent_schedule: !next } : j)));
    }
  };

  const toggleRow = (id) => {
    setSelected((s) => {
      const next = new Set(s);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleAll = () => {
    setSelected((s) => {
      // If every visible row is selected, clear. Otherwise select all visible.
      const allIds = visibleJobs.map((j) => j.id);
      const allSelected = allIds.length > 0 && allIds.every((id) => s.has(id));
      return allSelected ? new Set() : new Set(allIds);
    });
  };

  // Row-level Master Job Card PDF preview — fetches the PDF as a blob and
  // opens it in a side sheet without navigating away from the list.
  const openPdfPreview = async (job) => {
    setPreviewJob(job);
    setPreviewLoading(true);
    try {
      const res = await api.get(`/jobs/${job.id}/master-card.pdf`, { responseType: "blob" });
      if (previewUrl) URL.revokeObjectURL(previewUrl);
      setPreviewUrl(URL.createObjectURL(res.data));
    } catch (err) {
      toast.error(formatApiError(err));
      setPreviewJob(null);
    } finally {
      setPreviewLoading(false);
    }
  };
  const closePdfPreview = () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(null);
    setPreviewJob(null);
  };


  const applyBulk = async (patch) => {
    if (selected.size === 0) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/jobs/bulk-update", {
        job_ids: Array.from(selected),
        ...patch,
      });
      toast.success(`Updated ${data.modified} of ${data.requested} job${data.requested === 1 ? "" : "s"}`);
      setSelected(new Set());
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBulkBusy(false);
    }
  };

  const exportJobs = async () => {
    setExporting(true);
    try {
      const res = await api.get("/jobs/export.xlsx", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `Jobs Export ${new Date().toLocaleDateString("en-CA")}.xlsx`;
      a.click();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setExporting(false);
    }
  };

  return (
    <div>
      <PageHeader
        titleKey="page.orders.title"
        subtitleKey="page.orders.subtitle"
        subtitle="Order Management"
        title="Jobs"
        actions={
          canCreate && (
            <div className="flex items-center gap-2">
              <Dialog open={inqOpen} onOpenChange={setInqOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-inquiry-btn" variant="outline" className="rounded-sm border-[#FF5A00] text-[#FF5A00] hover:bg-orange-50">
                    <ClipboardList size={16} className="mr-1.5" /> New Inquiry
                  </Button>
                </DialogTrigger>
                <CreateJobDialog mode="inquiry" onCreated={() => { setInqOpen(false); setPage(0); setViewMode("inquiries"); load(); }} />
              </Dialog>
              <Dialog open={pasteOpen} onOpenChange={setPasteOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="bulk-paste-btn" variant="outline" size="icon" title="Import jobs from the Planning sheet (upload or paste)" className="rounded-sm border-neutral-300 text-neutral-700 hover:bg-neutral-100">
                    <ClipboardPaste size={16} />
                  </Button>
                </DialogTrigger>
                {/* The list refreshes itself after an import (lib/live.js) */}
                <PasteImportDialog />
              </Dialog>
              <Button
                data-testid="orders-export-btn"
                variant="outline"
                size="icon"
                title="Export every job as a Planning-sheet workbook — edit it and bring it back through Import"
                className="rounded-sm border-neutral-300 text-neutral-700 hover:bg-neutral-100"
                onClick={exportJobs}
                disabled={exporting}
              >
                <Download size={16} />
              </Button>
              <BulkStageImport />
              <Dialog open={open} onOpenChange={setOpen}>
                <DialogTrigger asChild>
                  <Button data-testid="create-job-btn" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
                    <Plus size={16} className="mr-1.5" /> New Job
                  </Button>
                </DialogTrigger>
                <CreateJobDialog onCreated={() => { setOpen(false); reload(); }} />
              </Dialog>
            </div>
          )
        }
      />
      <div className="p-8">
        {/* Inquiries ↔ Orders view switcher */}
        <div className="mb-4 flex items-center gap-1 border border-neutral-200 bg-white p-1 rounded-sm w-fit" data-testid="view-mode-switcher">
          {[
            { key: "all",        label: "All",        count: counts.all },
            { key: "inquiries",  label: "Inquiries",  count: counts.inquiries },
            { key: "orders",     label: "Orders",     count: counts.orders },
            ...(counts.not_in_pipeline || viewMode === "missing"
              ? [{ key: "missing", label: "Not in Pipeline", count: counts.not_in_pipeline || 0 }] : []),
          ].map(({ key, label, count }) => {
            const active = viewMode === key;
            return (
              <button
                key={key}
                data-testid={`view-mode-${key}`}
                onClick={() => onFilter(setViewMode)(key)}
                className={`px-4 py-1.5 text-sm rounded-sm font-medium transition-colors ${
                  active
                    ? "bg-[#FF5A00] text-white"
                    : "text-neutral-600 hover:bg-neutral-100"
                }`}
              >
                {label} <span className={`ml-1.5 font-mono-num text-xs ${active ? "text-orange-100" : "text-neutral-400"}`}>{count.toLocaleString("en-IN")}</span>
              </button>
            );
          })}
        </div>

        {/* Convert-to-Order dialog (mounted at page level so it survives row unmount on refetch) */}
        {convertFor && (
          <Dialog open={!!convertFor} onOpenChange={(v) => !v && setConvertFor(null)}>
            <ConvertToOrderDialog
              inquiry={convertFor}
              onDone={() => { setConvertFor(null); setPage(0); setViewMode("orders"); load(); }}
            />
          </Dialog>
        )}

        {/* Master Job Card PDF preview sheet — opened via the eye icon on any row */}
        <Sheet open={!!previewJob} onOpenChange={(v) => !v && closePdfPreview()}>
          <SheetContent side="right" className="w-full sm:max-w-3xl p-0 overflow-hidden flex flex-col" data-testid="orders-pdf-preview">
            <SheetHeader className="p-4 border-b border-neutral-200 shrink-0">
              <SheetTitle className="font-display text-sm">
                Master Job Card · <span className="font-mono-num text-neutral-600">{previewJob?.mpwo_number || previewJob?.job_number}</span>
              </SheetTitle>
              <p className="text-xs text-neutral-500 truncate">{previewJob?.customer_name} — {previewJob?.product_name}</p>
            </SheetHeader>
            <div className="flex-1 bg-neutral-100">
              {previewLoading && (
                <div className="flex items-center justify-center h-full text-sm text-neutral-500">Loading preview…</div>
              )}
              {!previewLoading && previewUrl && (
                <iframe
                  key={previewUrl}
                  src={previewUrl}
                  title="Master Job Card PDF"
                  className="w-full h-full border-0"
                  data-testid="orders-pdf-frame"
                />
              )}
            </div>
          </SheetContent>
        </Sheet>

        {/* Stale-inquiry follow-up nudge — visible whenever the backend reports any */}
        {staleCount > 0 && (
          <div
            data-testid="stale-nudge"
            className="mb-4 flex items-center gap-3 border border-amber-300 bg-amber-50 px-4 py-3 rounded-sm"
          >
            <AlertTriangle size={16} className="text-amber-700 shrink-0" />
            <div className="text-sm text-amber-900">
              <span className="font-semibold" data-testid="stale-nudge-count">{staleCount.toLocaleString("en-IN")}</span>{" "}
              new inquir{staleCount === 1 ? "y is" : "ies are"} <b>older than {STALE_THRESHOLD_DAYS} days</b> with no order type set —
              chase before they go cold.
            </div>
            <Button
              data-testid="stale-nudge-toggle"
              variant={staleOnly ? "default" : "outline"}
              size="sm"
              className={`ml-auto rounded-sm ${staleOnly ? "bg-amber-700 hover:bg-amber-800 text-white" : ""}`}
              onClick={() => onFilter(setStaleOnly)(!staleOnly)}
            >
              {staleOnly ? "Show all jobs" : "Show only stale"}
            </Button>
          </div>
        )}

        <div className="mb-4 flex flex-wrap gap-3 items-center">
          <div className="relative w-full md:w-auto">
            <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
            <Input
              data-testid="orders-search"
              placeholder="Search BPR, PO, customer, product…"
              className="pl-8 rounded-sm w-full md:w-72"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && search()}
            />
          </div>
          <Select value={statusFilter} onValueChange={onFilter(setStatusFilter)}>
            <SelectTrigger data-testid="status-filter" className="w-full md:w-56 rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All statuses</SelectItem>
              {/* Every department, same grouping as the dashboard's "Jobs in X" tiles — shown even at
                  0 so a department is always there to pick, not just the ones with jobs right now. */}
              {Object.entries(DEPT_STATUSES).map(([key, { label }]) => (
                <SelectItem key={key} value={`dept:${key}`}>
                  {label} · {(deptCounts[key] || 0).toLocaleString("en-IN")}
                </SelectItem>
              ))}
              {statusFilter.startsWith("ready:") && (
                <SelectItem value={statusFilter}>{readyForLabel(statusFilter.slice(6).split(",")[0])}</SelectItem>
              )}
              {/* Statuses outside the department groups above (dispatched, cancelled, …) — still
                  data-driven, since there's no fixed "department" for these. */}
              {statusList.filter((s) => s.status && !ALL_DEPT_STATUS_KEYS.has(s.status)).map((s) => (
                <SelectItem key={s.status} value={s.status}>
                  {readyForLabel(s.status)} · {s.count.toLocaleString("en-IN")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={inquiryFilter} onValueChange={onFilter(setInquiryFilter)}>
            <SelectTrigger data-testid="inquiry-filter" className="w-full md:w-44 rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All inquiries</SelectItem>
              <SelectItem value="New Inquiry">New Inquiry</SelectItem>
              <SelectItem value="Repeat Inquiry">Repeat Inquiry</SelectItem>
            </SelectContent>
          </Select>
          <Select value={orderTypeFilter} onValueChange={onFilter(setOrderTypeFilter)}>
            <SelectTrigger data-testid="order-type-filter" className="w-full md:w-40 rounded-sm">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All orders</SelectItem>
              <SelectItem value="New Order">New Order</SelectItem>
              <SelectItem value="Repeat Order">Repeat Order</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" onClick={search} className="rounded-sm">Apply</Button>
          <div className="ml-auto text-sm text-neutral-500">
            {loading ? (
              <span className="text-neutral-400">Loading…</span>
            ) : (
              <>
                <span className="font-mono-num font-bold text-neutral-900">{total.toLocaleString("en-IN")}</span>
                {staleOnly ? " stale" : ""} job{total === 1 ? "" : "s"}
              </>
            )}
          </div>
        </div>

        {/* Bulk action bar — appears when ≥1 job is checked */}
        {canBulk && selected.size > 0 && (
          <BulkActionBar
            count={selected.size}
            busy={bulkBusy}
            statuses={statusList.map(s => s.status).filter(Boolean)}
            machines={machines}
            operators={operators}
            onApply={applyBulk}
            onClear={() => setSelected(new Set())}
          />
        )}

        {/* Phone: one card per job (the 13-column table is unusable at 390px) */}
        <div className="md:hidden space-y-2" data-testid="orders-cards">
          {visibleJobs.map((j) => (
            <div
              key={j.id}
              data-testid={`order-card-${j.id}`}
              className={`border border-neutral-200 bg-white p-3 ${selected.has(j.id) ? "bg-orange-50/60" : ""}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="flex items-center gap-2 min-w-0">
                  {canBulk && (
                    <input
                      type="checkbox"
                      className="h-5 w-5 accent-[#FF5A00] shrink-0"
                      checked={selected.has(j.id)}
                      onChange={() => toggleRow(j.id)}
                      aria-label={`Select ${j.job_number}`}
                    />
                  )}
                  <QueueNo n={j.queue_no} />
                  {j.urgent_schedule && <UrgentBadge urgent />}
                  <Link to={`/orders/${j.id}`} className="font-mono-num text-sm font-semibold text-neutral-900 truncate">
                    {j.mpwo_number || j.job_number}
                  </Link>
                  <LotTag job={j} />
                  <PipelineTag job={j} />
                </div>
                <StatusBadge status={badgeStatus(j)} />
              </div>
              <Link to={`/orders/${j.id}`} className="block mt-2">
                <div className="text-sm font-medium">{j.customer_name}</div>
                <div className="text-sm text-neutral-600 truncate">{j.product_name}</div>
              </Link>
              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-neutral-500">
                <span>Qty <b className="font-mono-num text-neutral-900">{j.quantity?.toLocaleString("en-IN")}</b></span>
                {j.due_date && <span>Due <b className="text-neutral-900">{formatDate(j.due_date)}</b></span>}
                {canSeeFinance && <span>Revenue <b className="font-mono-num text-neutral-900">{formatINR(j.revenue)}</b></span>}
                {isStaleInquiry(j) && <span className="px-1 py-0.5 text-[9px] font-semibold uppercase rounded-sm bg-amber-200 text-amber-900">Stale</span>}
                <button type="button" onClick={() => openPdfPreview(j)} className="ml-auto inline-flex items-center gap-1 text-[#FF5A00] font-medium">
                  <Eye size={14} /> Job card
                </button>
              </div>
            </div>
          ))}
          {visibleJobs.length === 0 && (
            <div className="border border-neutral-200 bg-white p-6 text-center text-sm text-neutral-400">
              {loading ? "Loading jobs…" : loadError ? <>Couldn't load jobs — {loadError}. <button onClick={load} className="underline">Retry</button></> : "No jobs match the current filters."}
            </div>
          )}
        </div>

        <div className="hidden md:block border border-neutral-200 bg-white overflow-x-auto">
          <table className="w-full text-sm" data-testid="orders-table">
            <thead className="bg-neutral-50 label-caps">
              <tr>
                {canBulk ? (
                  <>
                    <th className="w-10 px-3 py-2.5 text-right">#</th>
                    <th className="w-10 px-3 py-2.5">
                      <input
                        type="checkbox"
                        data-testid="orders-select-all"
                        className="cursor-pointer accent-[#FF5A00]"
                        checked={visibleJobs.length > 0 && visibleJobs.every((j) => selected.has(j.id))}
                        onChange={toggleAll}
                        aria-label="Select all"
                      />
                    </th>
                  </>
                ) : (
                  <>
                    {/* No bulk actions for this role: in place of the checkbox column, each job's number in its
                        department's queue (a running number in a list that isn't one) — the operations head
                        moves a job by typing a new one, and marks it urgent beside it. */}
                    <th className="w-10 px-3 py-2.5 text-right">#</th>
                    {canSchedule && <th className="w-24 px-1 py-2.5 text-left">Urgent</th>}
                  </>
                )}
                <th className="text-left px-4 py-2.5">Job # / MPWO</th>
                <th className="text-left px-4 py-2.5">BPR</th>
                <th className="text-left px-4 py-2.5">Customer</th>
                <th className="text-left px-4 py-2.5">Product</th>
                <th className="text-left px-4 py-2.5">Prod. Code</th>
                <th className="text-right px-4 py-2.5">Qty</th>
                <th className="text-left px-4 py-2.5">Type</th>
                {canSeeFinance && <th className="text-right px-4 py-2.5">Revenue</th>}
                {canSeeFinance && <th className="text-right px-4 py-2.5">RM %</th>}
                {canSeeFinance && <th className="text-right px-4 py-2.5">Profit</th>}
                <th className="text-left px-4 py-2.5">Status</th>
                <th className="text-left px-4 py-2.5">Entry Date</th>
                <th className="text-left px-4 py-2.5">Due</th>
                <th className="w-10 px-2 py-2.5"></th>
              </tr>
            </thead>
            <tbody>
              {visibleJobs.map((j, i) => {
                const stale = isStaleInquiry(j);
                const checked = selected.has(j.id);
                // A job's real place in its queue (its own department's, on the mixed Scheduling Jobs list)
                // always wins when the server sent one — it holds steady under a search, unlike a row count.
                const jobQueueName = queue.name || j.queue;
                const jobQueueTotal = queue.name ? queue.total : j.queue_size;
                const number = j.queue_no ?? (queue.name ? null : page * PAGE_SIZE + i + 1);
                return (
                  <tr
                    key={j.id}
                    data-testid={`order-row-${j.id}`}
                    onClick={(e) => openRow(e, j)}
                    className={`border-t border-neutral-100 data-row cursor-pointer ${
                      checked ? "bg-orange-50/60" : j.urgent_schedule ? "bg-rose-50/70" : stale ? "bg-amber-50/50" : ""
                    }`}
                  >
                    {canBulk ? (
                      <>
                        <td className="w-10 px-3 py-2.5 text-right font-mono-num text-xs text-neutral-500">{number ?? "—"}</td>
                        <td className="w-10 px-3 py-2.5">
                          <input
                            type="checkbox"
                            data-testid={`order-select-${j.id}`}
                            className="cursor-pointer accent-[#FF5A00]"
                            checked={checked}
                            onChange={() => toggleRow(j.id)}
                            onClick={(e) => e.stopPropagation()}
                            aria-label={`Select ${j.job_number}`}
                          />
                        </td>
                      </>
                    ) : !(canSchedule && jobQueueName && j.queue_no) ? (
                      <td className="w-10 px-3 py-2.5 text-right font-mono-num text-xs text-neutral-500">{number ?? "—"}</td>
                    ) : editingRank === j.id ? (
                      <td className="w-10 px-3 py-2.5">
                        <input
                          type="number"
                          min={1}
                          max={jobQueueTotal}
                          autoFocus
                          defaultValue={j.queue_no}
                          data-testid={`order-rank-${j.id}`}
                          className="w-full bg-orange-50 text-right font-mono-num text-xs outline-none rounded-sm px-1 border border-[#FF5A00] [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                          onClick={(e) => e.stopPropagation()}
                          onFocus={(e) => e.target.select()}
                          onKeyDown={(e) => { if (e.key === "Enter") e.currentTarget.blur(); if (e.key === "Escape") setEditingRank(null); }}
                          onBlur={(e) => {
                            const v = Number(e.target.value);
                            if (v && v !== j.queue_no) reorderJob(j.id, v, jobQueueName);
                            else setEditingRank(null);
                          }}
                        />
                      </td>
                    ) : (
                      <td className="w-10 px-3 py-2.5">
                        <button
                          type="button"
                          data-testid={`order-rank-${j.id}`}
                          title={`#${j.queue_no} of ${jobQueueTotal} in ${jobQueueName === "planning" ? "Planning" : DEPT_STATUSES[jobQueueName]?.label || jobQueueName} — click to move it`}
                          className="w-full text-right font-mono-num text-xs text-neutral-500 hover:text-[#FF5A00] hover:underline"
                          onClick={(e) => { e.stopPropagation(); setEditingRank(j.id); }}
                        >
                          {j.queue_no}
                        </button>
                      </td>
                    )}
                    {canSchedule && (
                      <td className="w-24 px-1 py-2.5">
                        <select
                          data-testid={`order-urgent-${j.id}`}
                          value={j.urgent_schedule ? "urgent" : "scheduled"}
                          onClick={(e) => e.stopPropagation()}
                          onChange={(e) => { if ((e.target.value === "urgent") !== !!j.urgent_schedule) toggleUrgent(j); }}
                          className={`w-full text-[10px] font-semibold uppercase tracking-wide rounded-sm px-1.5 py-1 border outline-none cursor-pointer ${
                            j.urgent_schedule
                              ? "bg-rose-100 text-rose-800 border-rose-300"
                              : "bg-emerald-100 text-emerald-800 border-emerald-300"
                          }`}
                        >
                          <option value="scheduled">Scheduled</option>
                          <option value="urgent">Urgent</option>
                        </select>
                      </td>
                    )}
                    <td className="px-4 py-2.5 font-mono-num text-xs whitespace-nowrap">
                      <div className="flex items-center gap-1.5">
                        {!canSchedule && j.urgent_schedule && <UrgentBadge urgent />}
                        <Link to={`/orders/${j.id}?tab=production_flow&stage=${getProductionFocusStage(j)}`} data-testid={`job-link-${j.job_number}`}
                          title="Open this job's current production stage" className="hover:underline text-neutral-900">
                          {j.mpwo_number || j.job_number}
                        </Link>
                        <LotTag job={j} />
                        <PipelineTag job={j} />
                        {stale && (
                          <span
                            data-testid={`stale-flag-${j.id}`}
                            title={`New inquiry with no order type set (>${STALE_THRESHOLD_DAYS} days old)`}
                            className="inline-flex items-center gap-0.5 px-1 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded-sm bg-amber-200 text-amber-900 border border-amber-300"
                          >
                            <AlertTriangle size={9} /> Stale
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-2.5 font-mono-num text-xs text-neutral-500">{j.bpr_number || "—"}</td>
                    <td className="px-4 py-2.5">{j.customer_name}</td>
                    <td className="px-4 py-2.5 text-neutral-600 max-w-xs truncate">{j.product_name}</td>
                    <td className="px-4 py-2.5 font-mono-num text-xs text-neutral-500">{j.product_code || "—"}</td>
                    <td className="px-4 py-2.5 text-right font-mono-num">{j.quantity?.toLocaleString("en-IN")}</td>
                    <td className="px-4 py-2.5 text-xs text-neutral-500">{j.job_type || "—"}</td>
                    {canSeeFinance && <td className="px-4 py-2.5 text-right font-mono-num">{formatINR(j.revenue)}</td>}
                    {canSeeFinance && (
                      <td className={`px-4 py-2.5 text-right font-mono-num ${
                        !j.rm_pct ? "text-neutral-400" :
                        j.rm_pct > 70 ? "text-rose-700 font-semibold" :
                        j.rm_pct > 50 ? "text-amber-700" : "text-emerald-700"
                      }`}>
                        {j.rm_pct ? `${j.rm_pct}%` : "—"}
                      </td>
                    )}
                    {canSeeFinance && (
                      <td className={`px-4 py-2.5 text-right font-mono-num ${j.profit >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
                        {formatINR(j.profit)}
                      </td>
                    )}
                    <td className="px-4 py-2.5">
                      <Link
                        to={`/orders/${j.id}?tab=production_flow&stage=${getProductionFocusStage(j)}`}
                        title="Open this job's current production stage"
                        aria-label={`Open ${getJobDisplayStatus(j)} stage for ${j.mpwo_number || j.job_number}`}
                      >
                        <StatusBadge status={badgeStatus(j, getJobDisplayStatus(j))} />
                      </Link>
                    </td>
                    <td className="px-4 py-2.5 text-xs text-neutral-600 font-mono-num" data-testid={`order-entry-date-${j.id}`}>{formatDate(j.created_at)}</td>
                    <td className="px-4 py-2.5 text-xs text-neutral-600">{formatDate(j.due_date)}</td>
                    <td className="px-2 py-2.5">
                      <div className="flex items-center gap-1 justify-end">
                        <button
                          type="button"
                          data-testid={`preview-pdf-${j.id}`}
                          onClick={(e) => { e.stopPropagation(); openPdfPreview(j); }}
                          className="inline-flex items-center justify-center h-6 w-6 rounded-sm text-neutral-500 hover:text-[#FF5A00] hover:bg-orange-50 transition-colors"
                          title="Preview Master Job Card PDF"
                        >
                          <Eye size={13} />
                        </button>
                        {canBulk && (j.status || "").toLowerCase() === "enquiry" && !j.converted_from_inquiry_at && (
                          <button
                            type="button"
                            data-testid={`convert-inquiry-${j.id}`}
                            onClick={(e) => { e.stopPropagation(); setConvertFor(j); }}
                            className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-medium rounded-sm bg-[#FF5A00] text-white hover:bg-[#e04f00] shadow-sm"
                            title="Convert this inquiry into a confirmed order"
                          >
                            <ArrowRightCircle size={12} /> Convert
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {visibleJobs.length === 0 && (
                <tr><td colSpan={(canBulk ? 2 : 0) + (canSeeFinance ? 13 : 10) + 1} className="px-4 py-10 text-center text-neutral-400">
                  {loading ? (
                    <span className="inline-flex items-center gap-2"><span className="h-1.5 w-1.5 bg-[#FF5A00] rounded-full animate-pulse" />Loading jobs…</span>
                  ) : loadError ? (
                    <span className="text-rose-700">Couldn't load jobs — {loadError}. <button onClick={load} className="underline hover:text-rose-900">Retry</button></span>
                  ) : staleOnly ? (
                    "No stale inquiries in the current filter."
                  ) : (
                    "No jobs match this filter."
                  )}
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
        <Pager page={page} total={total} loading={loading} onPage={setPage} />
      </div>
    </div>
  );
}

function Pager({ page, total, loading, onPage }) {
  if (total <= PAGE_SIZE) return null;
  const last = Math.ceil(total / PAGE_SIZE) - 1;
  const from = page * PAGE_SIZE + 1;
  const to = Math.min(total, (page + 1) * PAGE_SIZE);
  return (
    <div className="mt-3 flex items-center justify-between gap-3 text-sm text-neutral-600" data-testid="orders-pager">
      <span className="font-mono-num">
        {from.toLocaleString("en-IN")}–{to.toLocaleString("en-IN")} of {total.toLocaleString("en-IN")}
      </span>
      <div className="flex items-center gap-2">
        <Button variant="outline" size="sm" className="rounded-sm" data-testid="orders-prev"
          disabled={loading || page === 0} onClick={() => onPage(page - 1)}>
          Previous
        </Button>
        <span className="font-mono-num text-xs text-neutral-500">{page + 1} / {last + 1}</span>
        <Button variant="outline" size="sm" className="rounded-sm" data-testid="orders-next"
          disabled={loading || page >= last} onClick={() => onPage(page + 1)}>
          Next
        </Button>
      </div>
    </div>
  );
}

// ============================================================================
// BULK ACTION BAR — floats above the table when ≥1 job is checked
// ============================================================================

function BulkActionBar({ count, busy, statuses, machines, operators, onApply, onClear }) {
  const [status, setStatus] = useState("");
  const [machineId, setMachineId] = useState("");
  const [operatorId, setOperatorId] = useState("");

  const apply = () => {
    const patch = {};
    if (status) patch.status = status;
    if (machineId) patch.assigned_machine_id = machineId;
    if (operatorId) patch.assigned_operator_id = operatorId;
    if (Object.keys(patch).length === 0) {
      toast.error("Pick a status, machine, or operator to apply");
      return;
    }
    onApply(patch);
    // Clear pickers so a second apply doesn't repeat the same change silently
    setStatus(""); setMachineId(""); setOperatorId("");
  };

  return (
    <div
      data-testid="bulk-action-bar"
      className="mb-4 flex flex-wrap items-center gap-2 border border-[#FF5A00] bg-orange-50/70 px-4 py-3 rounded-sm"
    >
      <div className="text-sm font-semibold text-[#FF5A00]" data-testid="bulk-count">
        {count} selected
      </div>
      <span className="text-neutral-400">·</span>
      <Select value={status} onValueChange={setStatus}>
        <SelectTrigger data-testid="bulk-status" className="w-52 rounded-sm h-9">
          <SelectValue placeholder="Change status…" />
        </SelectTrigger>
        <SelectContent>
          {statuses.map((s) => (
            <SelectItem key={s} value={s}>{s.replace(/_/g, " ")}</SelectItem>
          ))}
        </SelectContent>
      </Select>
      <Select value={machineId} onValueChange={setMachineId}>
        <SelectTrigger data-testid="bulk-machine" className="w-52 rounded-sm h-9">
          <SelectValue placeholder="Assign machine…" />
        </SelectTrigger>
        <SelectContent>
          {machines.map((m) => (
            <SelectItem key={m.id} value={m.id}>{m.code} · {m.name}</SelectItem>
          ))}
          {machines.length === 0 && <SelectItem value="none" disabled>No machines</SelectItem>}
        </SelectContent>
      </Select>
      <Select value={operatorId} onValueChange={setOperatorId}>
        <SelectTrigger data-testid="bulk-operator" className="w-52 rounded-sm h-9">
          <SelectValue placeholder="Assign operator…" />
        </SelectTrigger>
        <SelectContent>
          {operators.map((o) => (
            <SelectItem key={o.id} value={o.id}>{o.code} · {o.name}</SelectItem>
          ))}
          {operators.length === 0 && <SelectItem value="none" disabled>No operators</SelectItem>}
        </SelectContent>
      </Select>
      <Button
        data-testid="bulk-apply"
        onClick={apply}
        disabled={busy}
        className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00] h-9"
      >
        {busy ? "Applying…" : `Apply to ${count}`}
      </Button>
      <Button
        data-testid="bulk-clear"
        variant="ghost"
        size="sm"
        onClick={onClear}
        className="rounded-sm text-neutral-600 h-9"
      >
        <X size={14} className="mr-1" /> Clear
      </Button>
    </div>
  );
}

// ============================================================================
// CREATE JOB DIALOG — two modes: Manual Form (all Pipeline fields) + Paste from Excel
// ============================================================================

function CreateJobDialog({ onCreated, mode = "job" }) {
  const isInquiry = mode === "inquiry";
  return (
    <DialogContent className="max-w-5xl rounded-sm max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display flex items-center gap-2">
          {isInquiry ? <><ClipboardList size={20} className="text-[#FF5A00]" /> New Inquiry</> : "New Job"}
        </DialogTitle>
        {isInquiry ? (
          <p className="text-xs text-neutral-500 mt-1">
            Same details as a job — used for sample rolls and machine trials.
            A BPR number is issued automatically so the operator can produce the sample.
            Convert to an order when the PO comes through.
          </p>
        ) : (
          <p className="text-xs text-neutral-500 mt-1">
            Fill the Operations Work Order (Master Job Card) below. All spec fields, sign-offs and delivery details save with the job.
          </p>
        )}
      </DialogHeader>
      <div className="mt-2">
        <MasterJobCardForm onCreated={onCreated} mode={mode} />
      </div>
    </DialogContent>
  );
}

// Bulk import of jobs from the Planning sheet: upload the workbook or paste its rows.
function PasteImportDialog() {
  return (
    <DialogContent className="max-w-6xl rounded-sm max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display flex items-center gap-2">
          <ClipboardPaste size={18} className="text-[#FF5A00]" /> Import Jobs — Planning sheet
        </DialogTitle>
      </DialogHeader>
      <PlanningImport />
    </DialogContent>
  );
}

// ============================================================================
// CONVERT TO ORDER DIALOG — collects the order-only fields (PO number, confirmed
// quantity, unit price, due date). Everything else is carried forward from the
// inquiry so sales doesn't re-type customer / item / codes.
// ============================================================================

function ConvertToOrderDialog({ inquiry, onDone }) {
  const [form, setForm] = useState({
    po_number: "",
    po_date: new window.Date().toISOString().slice(0, 10),
    quantity: inquiry?.quantity || 1,
    unit_price: inquiry?.unit_price || "",
    due_date: "",
    material_cost_estimate: inquiry?.material_cost_estimate || "",
    order_type: (inquiry?.inquiry_type || "").startsWith("Repeat") ? "Repeat Order" : "New Order",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.po_number.trim()) { toast.error("PO Number is required"); return; }
    if (!form.quantity || Number(form.quantity) < 1) { toast.error("Order quantity must be at least 1"); return; }
    setSaving(true);
    try {
      const payload = {
        po_number: form.po_number.trim(),
        po_date: form.po_date || undefined,
        quantity: Number(form.quantity),
        unit_price: form.unit_price === "" ? undefined : Number(form.unit_price),
        due_date: form.due_date || undefined,
        material_cost_estimate: form.material_cost_estimate === "" ? undefined : Number(form.material_cost_estimate),
        order_type: form.order_type,
        notes: form.notes || undefined,
        target_status: "pending",
      };
      const { data } = await api.post(`/jobs/${inquiry.id}/convert-to-order`, payload);
      toast.success(`Inquiry converted → order ${data.job_number} (PO ${data.po_number})`);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <DialogContent className="max-w-2xl rounded-sm" data-testid="convert-inquiry-dialog">
      <DialogHeader>
        <DialogTitle className="font-display flex items-center gap-2">
          <ArrowRightCircle size={20} className="text-[#FF5A00]" />
          Convert Inquiry to Order
        </DialogTitle>
        <p className="text-xs text-neutral-500 mt-1">
          <span className="font-mono-num">{inquiry?.mpwo_number || inquiry?.job_number}</span>
          {" · "}
          {inquiry?.customer_name}
          {inquiry?.product_name ? ` · ${inquiry.product_name}` : ""}
        </p>
      </DialogHeader>

      {/* Carried-forward context — read-only chips so sales sees what's inherited */}
      <div className="bg-neutral-50 border border-neutral-200 rounded-sm p-3 text-xs space-y-1.5">
        <div className="text-neutral-500 font-semibold uppercase tracking-wider text-[10px]">Carried forward from inquiry</div>
        <div className="grid grid-cols-2 gap-x-6 gap-y-1 text-neutral-700">
          <div><span className="text-neutral-500">Customer:</span> <b>{inquiry?.customer_name}</b></div>
          <div><span className="text-neutral-500">Item:</span> <b>{inquiry?.product_name}</b></div>
          {inquiry?.product_code && <div><span className="text-neutral-500">Product Code:</span> {inquiry.product_code}</div>}
          {inquiry?.inquiry_type && <div><span className="text-neutral-500">Inquiry Type:</span> {inquiry.inquiry_type}</div>}
          {inquiry?.notes && <div className="col-span-2 text-neutral-600 pt-1 border-t border-neutral-200 mt-1 italic">{inquiry.notes}</div>}
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4 pt-2">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-caps block mb-1.5">PO Number *</label>
            <Input
              data-testid="conv-po_number"
              value={form.po_number}
              onChange={(e) => update("po_number", e.target.value)}
              placeholder="e.g. PO-2026-4001"
              required
              className="rounded-sm font-mono-num"
            />
          </div>
          <div>
            <label className="label-caps block mb-1.5">PO Date</label>
            <Input
              data-testid="conv-po_date"
              type="date"
              value={form.po_date}
              onChange={(e) => update("po_date", e.target.value)}
              className="rounded-sm font-mono-num"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-caps block mb-1.5">Confirmed Order Quantity *</label>
            <Input
              data-testid="conv-quantity"
              type="number"
              min={1}
              value={form.quantity}
              onChange={(e) => update("quantity", e.target.value)}
              required
              className="rounded-sm font-mono-num"
            />
            {inquiry?.quantity && Number(form.quantity) !== Number(inquiry.quantity) && (
              <div className="text-[10px] text-amber-700 mt-1">
                Inquiry asked for <span className="font-mono-num">{inquiry.quantity}</span> — confirmed differs by <span className="font-mono-num">{Number(form.quantity) - inquiry.quantity}</span>.
              </div>
            )}
          </div>
          <div>
            <label className="label-caps block mb-1.5">Unit Price (INR)</label>
            <Input
              data-testid="conv-unit_price"
              type="number"
              step="0.01"
              min={0}
              value={form.unit_price}
              onChange={(e) => update("unit_price", e.target.value)}
              placeholder="0.00"
              className="rounded-sm font-mono-num"
            />
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-caps block mb-1.5">Due Date</label>
            <Input
              data-testid="conv-due_date"
              type="date"
              value={form.due_date}
              onChange={(e) => update("due_date", e.target.value)}
              className="rounded-sm font-mono-num"
            />
          </div>
          <div>
            <label className="label-caps block mb-1.5">Material Cost Estimate</label>
            <Input
              data-testid="conv-material_cost"
              type="number"
              step="0.01"
              min={0}
              value={form.material_cost_estimate}
              onChange={(e) => update("material_cost_estimate", e.target.value)}
              placeholder="Optional"
              className="rounded-sm font-mono-num"
            />
          </div>
        </div>

        <div>
          <label className="label-caps block mb-1.5">Order Type</label>
          <div className="flex gap-1.5" data-testid="conv-order-type-chips">
            {["New Order", "Repeat Order"].map((opt) => {
              const active = form.order_type === opt;
              return (
                <button
                  key={opt}
                  type="button"
                  onClick={() => update("order_type", opt)}
                  className={`px-3 py-1.5 text-xs font-medium rounded-sm border transition-colors ${
                    active ? "bg-[#FF5A00] text-white border-[#FF5A00]" : "bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50"
                  }`}
                >
                  {opt}
                </button>
              );
            })}
          </div>
        </div>

        <div>
          <label className="label-caps block mb-1.5">Order Notes</label>
          <Textarea
            data-testid="conv-notes"
            value={form.notes}
            onChange={(e) => update("notes", e.target.value)}
            placeholder="Confirmation notes — special colour, delivery split, urgency, etc."
            rows={2}
            className="rounded-sm resize-none"
          />
        </div>

        <DialogFooter>
          <Button type="submit" disabled={saving} data-testid="conv-submit" className="rounded-sm bg-[#FF5A00] hover:bg-[#e04f00]">
            <ArrowRightCircle size={14} className="mr-1.5" />
            {saving ? "Converting…" : "Confirm Order"}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  );
}

